import styled, { keyframes } from "styled-components";
import React from "react";
import PropTypes from "prop-types";
import ScrollLock, { TouchScrollable } from "react-scrolllock";
import { colors, header, animationDurations } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import { useTopInsetBgDefault } from "../../organisms/Layout/context";
import { useNativeBackHandler } from "../../../helpers/nativeBackHandler";

const StyledSideTrayHolder = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6001;
  /* bottom: 0 instead of height: 100vh so the tray never extends
   * behind the mobile browser toolbar (100vh = large viewport) nor
   * past the fold on desktop where top is offset by the header. */
  bottom: 0;
  width: ${(props) => (props.isOpened ? props.width : "0px")};
  ${breakpoints.smDown} {
    width: ${(props) => (props.isOpened ? "100%" : "0px")};
    overflow-y: auto;
  }
  ${breakpoints.smUp} {
    top: ${header.desktopHeight};
  }
`;

const Backdrop = styled.div`
  ${breakpoints.smUp} {
    content: "";
    position: fixed;
    top: ${header.mobileHeight};
    left: 0;
    width: 100%;
    bottom: 0;
    background-color: ${colors.black};
    visibility: ${(props) => (props.isOpened ? "visible" : "hidden")};
    opacity: ${(props) => (props.isOpened ? "0.5" : "0")};
    cursor: ${(props) => (props.haveOnClickListener ? "pointer" : "unset")};
  }
  ${breakpoints.mdUp} {
    top: ${header.desktopHeight};
  }
`;

const sheetApearing = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const sheetDisappearing = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

/* No env(safe-area-inset-*) padding here — every consumer (BadgeInfo,
 * ChallengeInfo, OnboardingSideTray, Notifications, etc) already folds
 * env into its own header via safeAreaTopPaddingPlus(...), and a
 * second helping at this level just stacks. The atom centralises the
 * inset-stack push (useTopInsetBg below) and leaves layout to each
 * tray. */
const StyledSideTrayContent = styled.div`
  ${breakpoints.smUp} {
    width: ${(props) => props.width};
    position: fixed;
    top: ${header.mobileHeight};
    /* Fill from the header offset to the viewport bottom (height:
     * 100vh used to overflow the fold by the header height). */
    bottom: 0;
    height: auto;
  }
  position: static;
  top: 0;
  right: 0;
  width: 100%;
  /* On mobile the content sits statically inside the bottom-anchored
   * holder — 100% tracks the visible viewport, unlike 100vh which
   * hid the bottom of the tray behind iOS Safari's toolbar. */
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${(props) => props.color};
  display: ${(props) => (props.isOpened ? "block" : "none")};
  ${breakpoints.mdUp} {
    top: ${header.desktopHeight};
  }
`;

const AnimatingSideTrayContent = styled(StyledSideTrayContent)`
  animation: ${(props) => (!props.isOpened ? sheetDisappearing : sheetApearing)};
  animation-duration: ${animationDurations.sideTrayInSeconds}s;
`;

const SideTray = ({
  children,
  isOpened,
  color = colors.blue,
  className = "",
  onClickBackdrop = () => {},
  shouldAnimate = false,
  variant = "default",
}) => {
  const isLarge = variant === "large";
  const trayWidth = isLarge ? "700px" : "500px";
  useTopInsetBgDefault(isOpened ? color : null);

  /* Android hardware back closes the tray instead of popping the
   * underlying page (and on the dashboard with no back stack, exiting
   * the app entirely). OffCanvasTray has the equivalent registration
   * for its own family of trays — SideTray's consumers (saved-list
   * details, onboarding, notifications, badge info, etc) need their
   * own. */
  useNativeBackHandler(() => {
    if (isOpened) {
      onClickBackdrop();
      return true;
    }
    return false;
  }, [isOpened, onClickBackdrop]);

  return (
    <StyledSideTrayHolder isOpened={isOpened} width={trayWidth}>
      <Backdrop
        isOpened={isOpened}
        haveOnClickListener={!!onClickBackdrop}
        onClick={() => onClickBackdrop && onClickBackdrop()}
      />
      <ScrollLock isActive={isOpened}>
        <TouchScrollable>
          {shouldAnimate ? (
            <AnimatingSideTrayContent
              className={className}
              isOpened={isOpened}
              color={color}
              width={trayWidth}
            >
              {children}
            </AnimatingSideTrayContent>
          ) : (
            <StyledSideTrayContent className={className} isOpened={isOpened} color={color} width={trayWidth}>
              {children}
            </StyledSideTrayContent>
          )}
        </TouchScrollable>
      </ScrollLock>
    </StyledSideTrayHolder>
  );
};

SideTray.propTypes = {
  children: PropTypes.node.isRequired,
  isOpened: PropTypes.bool.isRequired,
  color: PropTypes.string,
  className: PropTypes.string,
  onClickBackdrop: PropTypes.func,
  shouldAnimate: PropTypes.bool,
  variant: PropTypes.oneOf(["default", "large"]),
};

export default SideTray;
